import { env } from '@/env';

/**
 * Cloudinary URL builder — applies `f_auto,q_auto,w_{size}` transforms.
 *
 * If `cloudinaryId` is null/empty, returns null and the consumer should
 * render a placeholder element. Never returns a broken `<img>` URL.
 */
export interface CloudinaryOptions {
  width?: number;
  height?: number;
  crop?: 'fill' | 'fit' | 'limit' | 'thumb';
  gravity?: 'auto' | 'face' | 'center';
}

const CLOUD_NAME = env.VITE_CLOUDINARY_CLOUD_NAME;

export function cloudinaryUrl(
  cloudinaryId: string | null | undefined,
  options: CloudinaryOptions = {}
): string {
  if (!cloudinaryId) {
    const width = options.width || 400;
    return `https://placehold.co/${width}x${Math.round(width * 1.25)}/1a1a2e/ffffff?text=QuickXKart`;
  }
  // If a fully-qualified URL was passed in, return as-is.
  if (cloudinaryId.startsWith('http')) return cloudinaryId;

  const transforms: string[] = ['f_auto', 'q_auto'];
  if (options.width) transforms.push(`w_${options.width}`);
  if (options.height) transforms.push(`h_${options.height}`);
  if (options.crop) transforms.push(`c_${options.crop}`);
  if (options.gravity) transforms.push(`g_${options.gravity}`);

  return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${transforms.join(',')}/${cloudinaryId}`;
}

/**
 * Image record shape — the backend may return EITHER:
 *   - `{ url, altText }`     (direct URL — e.g. Picsum seed during dev seeding)
 *   - `{ cloudinaryId, isPrimary }` (Cloudinary public ID — production)
 * `primaryImageUrl` handles both without forcing the caller to discriminate.
 */
type ProductImageRecord = {
  url?: string | null;
  cloudinaryId?: string | null;
  isPrimary?: boolean;
};

/** Picks the primary product image (or first image) and returns its URL. */
export function primaryImageUrl(
  images: ReadonlyArray<ProductImageRecord> | undefined,
  options: CloudinaryOptions = {}
): string {
  if (!images || images.length === 0) return cloudinaryUrl(null, options);
  const primary = images.find((i) => i.isPrimary) ?? images[0];
  if (!primary) return cloudinaryUrl(null, options);
  // Prefer the explicit URL if the backend already returns one; otherwise
  // build a Cloudinary URL from the public ID.
  const id = primary.url ?? primary.cloudinaryId ?? null;
  return cloudinaryUrl(id, options);
}

/**
 * Re-derive a Cloudinary or Picsum URL at a different width. Used to assemble
 * `srcset` for responsive images. For other host URLs the original URL is
 * returned unchanged.
 */
function urlAtWidth(srcOrId: string | null, width: number, height?: number): string | null {
  if (!srcOrId) return null;

  if (srcOrId.startsWith('http')) {
    // Picsum: https://picsum.photos/seed/{seed}/{w}/{h}
    const picsum = srcOrId.match(/^https:\/\/picsum\.photos\/(seed\/[^/]+\/)?(\d+)\/(\d+)(.*)$/);
    if (picsum) {
      const seed = picsum[1] ?? '';
      const h = height ?? Math.round(width * 1.25);
      const tail = picsum[4] ?? '';
      return `https://picsum.photos/${seed}${width}/${h}${tail}`;
    }
    // Cloudinary URL passed in directly — try to substitute the width transform.
    const cld = srcOrId.match(/^(https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)([^/]+)\/(.+)$/);
    if (cld) {
      const transforms = (cld[2] ?? '')
        .split(',')
        .filter((t) => !t.startsWith('w_'))
        .concat([`w_${width}`])
        .join(',');
      return `${cld[1]}${transforms}/${cld[3]}`;
    }
    // Unknown host — can't rewrite, return as-is.
    return srcOrId;
  }

  // Bare Cloudinary public ID — build via cloudinaryUrl.
  const opts: CloudinaryOptions = { width };
  if (height) opts.height = height;
  return cloudinaryUrl(srcOrId, opts);
}

/**
 * Build a responsive `srcSet` string for a product image across common DPR
 * widths. Pair with a `sizes` attribute that matches your grid breakpoints.
 *
 * Example: <img src={src} srcSet={srcSet} sizes="(min-width: 1024px) 25vw, ..." />
 */
export function productImageSrcSet(
  images: ReadonlyArray<ProductImageRecord> | undefined,
  baseWidth = 400
): { src: string; srcSet: string } {
  if (!images || images.length === 0) {
    return { src: cloudinaryUrl(null, { width: baseWidth }), srcSet: '' };
  }
  const primary = images.find((i) => i.isPrimary) ?? images[0];
  if (!primary) {
    return { src: cloudinaryUrl(null, { width: baseWidth }), srcSet: '' };
  }
  const id = primary.url ?? primary.cloudinaryId ?? null;
  // 3 widths cover the typical grid range: phone full-width, tablet half, desktop quarter.
  const widths = [320, 480, 640, 800];
  const set = widths
    .map((w) => {
      const u = urlAtWidth(id, w);
      return u ? `${u} ${w}w` : null;
    })
    .filter(Boolean)
    .join(', ');
  return {
    src: urlAtWidth(id, baseWidth) ?? cloudinaryUrl(null, { width: baseWidth }),
    srcSet: set,
  };
}
